import React from 'react';
import { Typography, Card, Divider, Space, Button } from 'antd';
import { CalendarOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;

function PrivacyPolicy() {
    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <Title level={1} className="!text-white !mb-4">
                        Privacy Policy
                    </Title>
                    <Paragraph className="!text-blue-100 !text-lg">
                        Your privacy is important to us. This policy explains how we collect, use, and protect your information.
                    </Paragraph>
                    <Space className="mt-4">
                        <Text className="text-blue-100">
                            <CalendarOutlined className="mr-1" />
                            Last updated: January 27, 2025
                        </Text>
                    </Space>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <Card>
                    <div className="prose prose-lg max-w-none">
                        {/* Introduction */}
                        <section className="mb-8">
                            <Title level={2}>Introduction</Title>
                            <Paragraph>
                                Spoon & Spice ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, use our mobile application, or engage with our catering services.
                            </Paragraph>
                            <Paragraph>
                                By using our services, you agree to the collection and use of information in accordance with this policy. If you do not agree with the terms of this Privacy Policy, please do not access or use our services.
                            </Paragraph>
                        </section>

                        <Divider />

                        {/* Information We Collect */}
                        <section className="mb-8">
                            <Title level={2}>Information We Collect</Title>
                            
                            <Title level={3}>Personal Information</Title>
                            <Paragraph>
                                We may collect personal information that you voluntarily provide to us when you:
                            </Paragraph>
                            <ul>
                                <li>Create an account or register for our services</li>
                                <li>Place orders for catering services</li>
                                <li>Contact us for customer support</li>
                                <li>Subscribe to our newsletters or promotional communications</li>
                                <li>Participate in surveys, contests, or promotions</li>
                            </ul>
                            
                            <Paragraph>
                                This information may include:
                            </Paragraph>
                            <ul>
                                <li>Name, email address, phone number, and mailing address</li>
                                <li>Payment information (processed securely through third-party payment processors)</li>
                                <li>Event details (date, time, location, guest count)</li>
                                <li>Dietary preferences and special requirements</li>
                                <li>Communication preferences</li>
                            </ul>

                            <Title level={3}>Automatically Collected Information</Title>
                            <Paragraph>
                                We automatically collect certain information when you use our services:
                            </Paragraph>
                            <ul>
                                <li>Device information (IP address, browser type, operating system)</li>
                                <li>Usage data (pages visited, time spent, features used)</li>
                                <li>Location data (with your permission)</li>
                                <li>Cookies and similar tracking technologies</li>
                            </ul>
                        </section>

                        <Divider />

                        {/* How We Use Information */}
                        <section className="mb-8">
                            <Title level={2}>How We Use Your Information</Title>
                            <Paragraph>
                                We use the collected information for various purposes:
                            </Paragraph>
                            
                            <Title level={3}>Service Provision</Title>
                            <ul>
                                <li>Process and fulfill your catering orders</li>
                                <li>Connect you with verified catering vendors</li>
                                <li>Provide customer support and respond to inquiries</li>
                                <li>Send order confirmations and delivery updates</li>
                            </ul>

                            <Title level={3}>Communication</Title>
                            <ul>
                                <li>Send important service-related notifications</li>
                                <li>Provide marketing communications (with your consent)</li>
                                <li>Send newsletters and promotional offers</li>
                                <li>Conduct surveys and gather feedback</li>
                            </ul>

                            <Title level={3}>Improvement and Analytics</Title>
                            <ul>
                                <li>Analyze usage patterns to improve our services</li>
                                <li>Develop new features and functionality</li>
                                <li>Conduct research and analytics</li>
                                <li>Ensure platform security and prevent fraud</li>
                            </ul>
                        </section>

                        <Divider />

                        {/* Information Sharing */}
                        <section className="mb-8">
                            <Title level={2}>Information Sharing and Disclosure</Title>
                            <Paragraph>
                                We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:
                            </Paragraph>

                            <Title level={3}>Service Providers</Title>
                            <Paragraph>
                                We may share information with trusted third-party service providers who assist us in operating our platform, including:
                            </Paragraph>
                            <ul>
                                <li>Catering vendors (to fulfill your orders)</li>
                                <li>Payment processors (to process payments)</li>
                                <li>Delivery services (to coordinate food delivery)</li>
                                <li>Analytics providers (to analyze usage patterns)</li>
                                <li>Customer support tools (to provide assistance)</li>
                            </ul>

                            <Title level={3}>Legal Requirements</Title>
                            <Paragraph>
                                We may disclose information when required by law or to:
                            </Paragraph>
                            <ul>
                                <li>Comply with legal obligations</li>
                                <li>Respond to lawful requests from authorities</li>
                                <li>Protect our rights and property</li>
                                <li>Ensure user safety and prevent fraud</li>
                            </ul>

                            <Title level={3}>Business Transfers</Title>
                            <Paragraph>
                                In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the transaction.
                            </Paragraph>
                        </section>

                        <Divider />

                        {/* Data Security */}
                        <section className="mb-8">
                            <Title level={2}>Data Security</Title>
                            <Paragraph>
                                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                            </Paragraph>
                            <ul>
                                <li>Encryption of sensitive data in transit and at rest</li>
                                <li>Regular security assessments and updates</li>
                                <li>Access controls and authentication mechanisms</li>
                                <li>Secure data storage and backup procedures</li>
                                <li>Employee training on data protection practices</li>
                            </ul>
                            <Paragraph>
                                However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
                            </Paragraph>
                        </section>

                        <Divider />

                        {/* Your Rights */}
                        <section className="mb-8">
                            <Title level={2}>Your Rights and Choices</Title>
                            <Paragraph>
                                You have certain rights regarding your personal information:
                            </Paragraph>

                            <Title level={3}>Access and Portability</Title>
                            <ul>
                                <li>Request access to your personal information</li>
                                <li>Request a copy of your data in a portable format</li>
                                <li>Review and update your account information</li>
                            </ul>

                            <Title level={3}>Correction and Deletion</Title>
                            <ul>
                                <li>Request correction of inaccurate information</li>
                                <li>Request deletion of your personal information</li>
                                <li>Close your account (subject to legal and contractual obligations)</li>
                            </ul>

                            <Title level={3}>Communication Preferences</Title>
                            <ul>
                                <li>Opt out of marketing communications</li>
                                <li>Manage notification preferences</li>
                                <li>Unsubscribe from newsletters</li>
                            </ul>

                            <Paragraph>
                                To exercise these rights, please contact us using the information provided in the "Contact Us" section below.
                            </Paragraph>
                        </section>

                        <Divider />

                        {/* Cookies */}
                        <section className="mb-8">
                            <Title level={2}>Cookies and Tracking Technologies</Title>
                            <Paragraph>
                                We use cookies and similar tracking technologies to enhance your experience on our platform. Cookies are small text files stored on your device that help us:
                            </Paragraph>
                            <ul>
                                <li>Remember your preferences and settings</li>
                                <li>Analyze website traffic and usage patterns</li>
                                <li>Provide personalized content and recommendations</li>
                                <li>Improve platform functionality and performance</li>
                            </ul>
                            <Paragraph>
                                You can control cookie settings through your browser preferences. However, disabling cookies may affect the functionality of our services.
                            </Paragraph>
                        </section>

                        <Divider />

                        {/* Third-Party Links */}
                        <section className="mb-8">
                            <Title level={2}>Third-Party Links</Title>
                            <Paragraph>
                                Our platform may contain links to third-party websites or services. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
                            </Paragraph>
                        </section>

                        <Divider />

                        {/* Children's Privacy */}
                        <section className="mb-8">
                            <Title level={2}>Children's Privacy</Title>
                            <Paragraph>
                                Our services are not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If we become aware that we have collected personal information from a child under 13, we will take steps to delete such information.
                            </Paragraph>
                        </section>

                        <Divider />

                        {/* International Transfers */}
                        <section className="mb-8">
                            <Title level={2}>International Data Transfers</Title>
                            <Paragraph>
                                Your information may be transferred to and processed in countries other than your country of residence. We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards to protect your information.
                            </Paragraph>
                        </section>

                        <Divider />

                        {/* Policy Updates */}
                        <section className="mb-8">
                            <Title level={2}>Changes to This Privacy Policy</Title>
                            <Paragraph>
                                We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by:
                            </Paragraph>
                            <ul>
                                <li>Posting the updated policy on our website</li>
                                <li>Sending email notifications to registered users</li>
                                <li>Displaying prominent notices on our platform</li>
                            </ul>
                            <Paragraph>
                                Your continued use of our services after the effective date of any changes constitutes acceptance of the updated policy.
                            </Paragraph>
                        </section>

                        <Divider />

                        {/* Contact Information */}
                        <section className="mb-8">
                            <Title level={2}>Contact Us</Title>
                            <Paragraph>
                                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
                            </Paragraph>
                            
                            <Card className="bg-gray-50">
                                <Space direction="vertical" size="large" className="w-full">
                                    <div>
                                        <Text strong className="block mb-2">Email:</Text>
                                        <Space>
                                            <MailOutlined />
                                            <a href="mailto:privacy@spoonandspice.com">privacy@spoonandspice.com</a>
                                        </Space>
                                    </div>
                                    
                                    <div>
                                        <Text strong className="block mb-2">Phone:</Text>
                                        <Space>
                                            <PhoneOutlined />
                                            <a href="tel:+917483423925">+91 7483423925</a>
                                        </Space>
                                    </div>
                                    
                                    <div>
                                        <Text strong className="block mb-2">Address:</Text>
                                        <Text>
                                            Spoon & Spice Private Limited<br />
                                            123 Business District<br />
                                            Mumbai, Maharashtra 400001<br />
                                            India
                                        </Text>
                                    </div>
                                </Space>
                            </Card>
                        </section>

                        {/* Footer */}
                        <div className="text-center mt-12 pt-8 border-t">
                            <Paragraph className="!text-gray-600">
                                This Privacy Policy is effective as of January 27, 2025, and was last updated on January 27, 2025.
                            </Paragraph>
                            <Space className="mt-4">
                                <Button type="primary" href="/contact">
                                    Contact Us
                                </Button>
                                <Button href="/terms-of-service">
                                    Terms of Service
                                </Button>
                            </Space>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
}

export default PrivacyPolicy;
